// Takes (docs/refactor-plan.md, Phase 5): a bar can hold other versions of itself, and a rule picks
// which one plays each time through. Old bars have no takes and play exactly as they did.
//   measure.takes     [{ id, score, ref, how, similarity }]: every version of the bar, the one it was
//                     first among them ("t1"). A take's score is that one bar ({ measures: [{ timeSig,
//                     parts }] }); ref, how and similarity say where it came from (as families do,
//                     src/lib/ideas/kinship.js) and how alike it is to the first, out of 100.
//   measure.takeId    the take the bar shows: its notes are the bar's own (the copy in `takes` is
//                     brought up to date when another is shown).
//   measure.takeRule  { play, minSimilarity, pinned }: how one is picked ("shuffle", the default:
//                     each once in a new order every round; "order"; "adaptive": the fewest notes
//                     first, harder as you play it again; "sections": the bar as shown, and a take
//                     where a section ends, as a fill), the least alike a take may be to play (null:
//                     any), and the one that always plays (null: the rule picks).
export const TAKE_RULES = [["shuffle", "Shuffle"], ["order", "In order"], ["adaptive", "Harder as you practice"], ["sections", "Fills before each section"]];
export const MAX_TAKES = 8;
const DEFAULT_RULE = { play: "shuffle", minSimilarity: null, pinned: null };
const clone = (value) => JSON.parse(JSON.stringify(value));
const number = (id) => Number(String(id).replace(/^t/, "")) || 0;
const notesIn = (bar) => bar.parts.reduce((sum, part) => sum + part.voices.reduce((count, voice) => count + voice.notes.filter((note) => note.notes.length).length, 0), 0);
const sameMeter = (a, b) => a?.timeSig?.num === b?.timeSig?.num && a?.timeSig?.type === b?.timeSig?.type;

// A bar's takes, each with the bar it plays ({ timeSig, parts }); the shown one is the bar itself.
export function takesOf(measure) {
  if (!measure?.takes?.length) return [];
  return measure.takes.map((take) => ({ ...take, bar: take.id === measure.takeId ? { timeSig: measure.timeSig, parts: measure.parts } : take.score.measures[0] }));
}
// "Take 2 of 5": which one the bar shows, and how many it has.
export function takeShown(measure) {
  const takes = measure?.takes || [];
  return takes.length > 1 ? { number: takes.findIndex((take) => take.id === measure.takeId) + 1, count: takes.length } : null;
}

// The bar with more takes: each { measure (one bar) or score (its first bar), ref, how, similarity }
// in the bar's time signature (others can't stand in for it). The bar as it is becomes take 1.
export function withTakes(measure, added = []) {
  const first = measure.takes?.length ? measure.takes : [{ id: "t1", score: { measures: [{ timeSig: clone(measure.timeSig), parts: clone(measure.parts) }] }, similarity: 100 }];
  let next = Math.max(...first.map((take) => number(take.id))) + 1;
  const more = added.map((take) => ({ ...take, bar: take.measure || take.score?.measures?.[0] })).filter((take) => sameMeter(take.bar, measure))
    .map(({ bar, ref = null, how = null, similarity = null }) => ({ id: `t${next++}`, score: { measures: [{ timeSig: clone(measure.timeSig), parts: clone(bar.parts) }] },
      ...(ref ? { ref } : {}), ...(how ? { how } : {}), ...(Number.isFinite(similarity) ? { similarity: Math.round(similarity) } : {}) }));
  if (!more.length) return measure;
  return { ...measure, takes: [...first, ...more].slice(0, MAX_TAKES), takeId: measure.takeId || "t1", takeRule: { ...DEFAULT_RULE, ...measure.takeRule } };
}
// The bar showing another of its takes: the one shown keeps its notes (edits included).
export function showingTake(measure, id) {
  const take = measure.takes?.find((item) => item.id === id);
  if (!take || id === measure.takeId) return measure;
  const takes = measure.takes.map((item) => item.id === measure.takeId ? { ...item, score: { measures: [{ timeSig: clone(measure.timeSig), parts: clone(measure.parts) }] } } : item);
  return { ...measure, parts: clone(take.score.measures[0].parts), takes, takeId: id };
}
// The next take in turn ("Take 3 of 5" after "Take 2 of 5", then back to the first).
export const nextTake = (measure) => {
  const takes = measure.takes || [], at = takes.findIndex((take) => take.id === measure.takeId);
  return takes.length > 1 ? takes[(at + 1) % takes.length].id : null;
};
// The bar without one of its takes; without the one it shows, it shows the next. With one left, it's
// a bar like any other again.
export function withoutTake(measure, id) {
  if (!measure.takes?.some((take) => take.id === id)) return measure;
  const shown = id === measure.takeId ? showingTake(measure, nextTake(measure)) : measure;
  const takes = shown.takes.filter((take) => take.id !== id);
  if (takes.length < 2) { const { takes: _, takeId, takeRule, ...plain } = shown; return plain; }
  return { ...shown, takes, takeRule: { ...shown.takeRule, pinned: shown.takeRule?.pinned === id ? null : shown.takeRule?.pinned ?? null } };
}
// The bar with its rule changed ({ play, minSimilarity, pinned }, any of them).
export function withRule(measure, rule = {}) {
  if (!measure.takes?.length) return measure;
  const next = { ...DEFAULT_RULE, ...measure.takeRule };
  if (rule.play !== undefined && TAKE_RULES.some(([play]) => play === rule.play)) next.play = rule.play;
  if (rule.minSimilarity !== undefined) next.minSimilarity = rule.minSimilarity == null ? null : Math.max(0, Math.min(100, Math.round(Number(rule.minSimilarity) || 0)));
  if (rule.pinned !== undefined) next.pinned = rule.pinned && measure.takes.some((take) => take.id === rule.pinned) ? rule.pinned : null;
  return { ...measure, takeRule: next };
}

// A number from 0 to 1 for a seed, the same each time (mulberry32).
function seeded(seed) {
  let t = (seed + 0x6D2B79F5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
// The takes in a new order for each round (`round` 0, 1, 2…), every one once a round.
function shuffled(takes, round, salt) {
  const order = [...takes];
  for (let index = order.length - 1; index > 0; index--) {
    const other = Math.floor(seeded(round * 7919 + salt * 104729 + index) * (index + 1));
    [order[index], order[other]] = [order[other], order[index]];
  }
  return order;
}

// The parts a bar plays its `time`-th time through a playing (0 first), on the `pass`-th playing
// (each start of playback is one), given whether a section ends with it.
export function takeToPlay(measure, { time = 0, pass = 0, sectionEnds = false, salt = 0 } = {}) {
  const takes = takesOf(measure);
  if (takes.length < 2) return measure.parts;
  const rule = { ...DEFAULT_RULE, ...measure.takeRule }, shown = takes.find((take) => take.id === measure.takeId) || takes[0];
  const pinned = rule.pinned && takes.find((take) => take.id === rule.pinned);
  if (pinned) return pinned.bar.parts;
  const playable = takes.filter((take) => take.id === takes[0].id || rule.minSimilarity == null || (take.similarity ?? 0) >= rule.minSimilarity);
  const turn = pass + time;
  let take;
  if (rule.play === "order") take = playable[turn % playable.length];
  else if (rule.play === "adaptive") { const easiest = [...playable].sort((a, b) => notesIn(a.bar) - notesIn(b.bar)); take = easiest[Math.min(turn, easiest.length - 1)]; }
  else if (rule.play === "sections") { const fills = playable.filter((item) => item.id !== shown.id); take = sectionEnds && fills.length ? fills[turn % fills.length] : shown; }
  else take = shuffled(playable, Math.floor(turn / playable.length), salt)[turn % playable.length];
  return (take || shown).bar.parts;
}
